'use client';

import cart from 'cart';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CustomDropdown from '@/components/CustomDropdown';
import { TailSpin } from 'react-loader-spinner';

type Currency = {
  code: string;
  symbol: string;
  name: string;
  countryCode: string;
  countryName: string;
};

// Country list for shipping - with all supported countries
const countries = [
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', currency: 'USD', isEuropean: false },
  { code: 'OM', name: 'Oman', flag: '🇴🇲', currency: 'USD', isEuropean: false },
  { code: 'BH', name: 'Bahrain', flag: '🇧🇭', currency: 'USD', isEuropean: false },
  { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', currency: 'USD', isEuropean: false },
  { code: 'QA', name: 'Qatar', flag: '🇶🇦', currency: 'USD', isEuropean: false },
  { code: 'KW', name: 'Kuwait', flag: '🇰🇼', currency: 'USD', isEuropean: false },

  { code: 'BE', name: 'Belgium', flag: '🇧🇪', currency: 'EUR', isEuropean: true },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱', currency: 'EUR', isEuropean: true },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', currency: 'USD', isEuropean: true },
  { code: 'ES', name: 'Spain', flag: '🇪🇸', currency: 'EUR', isEuropean: true },
  { code: 'IT', name: 'Italy', flag: '🇮🇹', currency: 'EUR', isEuropean: true },
  { code: 'AT', name: 'Austria', flag: '🇦🇹', currency: 'EUR', isEuropean: true },
  { code: 'FR', name: 'France', flag: '🇫🇷', currency: 'EUR', isEuropean: true },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', currency: 'EUR', isEuropean: true },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪', currency: 'EUR', isEuropean: true },
  { code: 'NO', name: 'Norway', flag: '🇳🇴', currency: 'EUR', isEuropean: true },
  { code: 'FI', name: 'Finland', flag: '🇫🇮', currency: 'EUR', isEuropean: true },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰', currency: 'EUR', isEuropean: true },
  { code: 'IS', name: 'Iceland', flag: '🇮🇸', currency: 'EUR', isEuropean: true },
  { code: 'PL', name: 'Poland', flag: '🇵🇱', currency: 'EUR', isEuropean: true },

  { code: 'US', name: 'United States', flag: '🇺🇸', currency: 'USD', isEuropean: false },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', currency: 'USD', isEuropean: false },

  { code: 'JO', name: 'Jordan', flag: '🇯🇴', currency: 'USD', isEuropean: false },
  { code: 'IL', name: 'Israel', flag: '🇮🇱', currency: 'ILS', isEuropean: false },
];

// Language options
const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
];

// Available currencies
const allCurrencies: Currency[] = [
  { code: 'USD', symbol: '$', name: 'US Dollar', countryCode: 'US', countryName: 'United States' },
  { code: 'EUR', symbol: '€', name: 'Euro', countryCode: 'BE', countryName: 'Belgium' },
  { code: 'ILS', symbol: '₪', name: 'Israeli Shekel', countryCode: 'IL', countryName: 'Israel' },
];

// Cart pages that require redirect
const CART_PAGES = ['/cart', '/cart/address', '/cart/payment'];

export default function CurrencyDropdown() {
  const router = useRouter();
  const { locale, query, pathname } = router;
  const { t } = useTranslation('common');

  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(null);
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);
  const [selectedLanguage, setSelectedLanguage] = useState(languages.find(l => l.code === locale) || languages[0]);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<{ 
    country: any;
    currency: Currency;
    changes: {
      countryChanged: boolean;
    }
  } | null>(null);
  const [tempPreferences, setTempPreferences] = useState<{
    currency: Currency | null;
    country: any;
    language: any;
  }>({
    currency: null,
    country: countries[0],
    language: languages.find(l => l.code === locale) || languages[0]
  });
  
  // Add loading state for country change
  const [isProcessingCountryChange, setIsProcessingCountryChange] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const preferencesRef = useRef<HTMLDivElement>(null);
  const confirmationRef = useRef<HTMLDivElement>(null);

  // Helper function to check if current page is a cart page
  const isCartPage = () => {
    return CART_PAGES.includes(pathname);
  };

  // Get user's country from saved choice
  const getUserCountryCode = (): string | null => {
    if (typeof window === 'undefined') return null;
    
    const userChoice = localStorage.getItem('user-locale-choice');
    if (userChoice) {
      try {
        const choice = JSON.parse(userChoice);
        return choice.countryCode || null;
      } catch (error) {
        console.error('Error parsing user choice:', error);
        return null;
      }
    }
    return null;
  };

  // Get available currencies for a country
  // - Israel: only ILS
  // - All other countries: both USD and EUR (users can freely switch)
  const getAvailableCurrenciesForCountry = (countryCode: string | null): Currency[] => {
    if (countryCode === 'IL') {
      // Israel can only use ILS
      return allCurrencies.filter(currency => currency.code === 'ILS');
    } else {
      // All other countries can use both USD and EUR - FREE SWITCHING
      return allCurrencies.filter(currency => currency.code === 'USD' || currency.code === 'EUR');
    }
  };

  // Get default currency based on country (for initial selection only)
  const getDefaultCurrencyForCountry = (countryCode: string): string => {
    const country = countries.find(c => c.code === countryCode);
    return country?.currency || 'USD';
  };

  // Function to get available countries - Always return all countries
  const getAvailableCountries = () => {
    return countries;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsPreferencesOpen(false);
      }
      if (preferencesRef.current && !preferencesRef.current.contains(event.target as Node)) {
        setIsPreferencesOpen(false);
      }
      if (confirmationRef.current && !confirmationRef.current.contains(event.target as Node)) {
        setShowConfirmation(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const userCountryCode = getUserCountryCode();
    const availableCountries = getAvailableCountries();
    
    const queryCurrency = query.currency as string | undefined;
    const savedCode = localStorage.getItem('user-currency-selection');
    const userChoice = localStorage.getItem('user-locale-choice');

    let activeCode = queryCurrency || savedCode;

    // If no currency in URL or localStorage, check user locale choice
    if (!activeCode && userChoice) {
      const choice = JSON.parse(userChoice);
      activeCode = choice.currency;
    }

    // Find the currency
    let foundCurrency : any = allCurrencies.find((c) => c.code === activeCode);
    
    // If no currency found, get default for user's country
    if (!foundCurrency && userCountryCode) {
      const defaultCurrencyCode = getDefaultCurrencyForCountry(userCountryCode);
      foundCurrency = allCurrencies.find((c) => c.code === defaultCurrencyCode);
    }
    
    // Still no currency? Use USD
    if (!foundCurrency) {
      foundCurrency = allCurrencies.find(c => c.code === 'USD');
    }

    if (foundCurrency) {
      setSelectedCurrency(foundCurrency);
      
      // Find country - prefer user's saved country, otherwise use currency's country
      let foundCountry : any;
      
      if (userChoice) {
        try {
          const choice = JSON.parse(userChoice);
          foundCountry = availableCountries.find(country => country.code === choice.countryCode);
        } catch (error) {
          console.error('Error parsing user choice:', error);
        }
      }
      
      // If no country from user choice, use currency's country or first available
      if (!foundCountry) {
        foundCountry = availableCountries.find(country => country.code === foundCurrency!.countryCode) || 
                       availableCountries[0];
      }
      
      if (foundCountry) {
        setSelectedCountry(foundCountry);
        // Update cart with both currency and country
        cart.updateCurrency(foundCurrency.code, foundCountry.code);
      }
      
      // Update tempPreferences
      setTempPreferences(prev => ({
        ...prev,
        currency: foundCurrency || null,
        country: foundCountry || countries[0]
      }));
    }

    // Set country and language from user choice or defaults
    if (userChoice) {
      const choice = JSON.parse(userChoice);
      const foundCountry = countries.find(c => c.code === choice.countryCode);
      if (foundCountry) {
        setSelectedCountry(foundCountry);
      }
      
      // Language should ONLY be set from current locale or saved language preference
      const foundLanguage = languages.find(l => l.code === locale);
      if (foundLanguage) {
        setSelectedLanguage(foundLanguage);
        // Also update tempPreferences with current language
        setTempPreferences(prev => ({
          ...prev,
          language: foundLanguage
        }));
      }
    }
  }, [query.currency, locale]);

  useEffect(() => {
    const handleLocaleChange = (event: any) => {
      const newChoice = event.detail;
      if (newChoice?.currency && newChoice?.countryCode) {
        const foundCurrency = allCurrencies.find((c) => c.code === newChoice.currency);
        const foundCountry = countries.find(c => c.code === newChoice.countryCode);
        const foundLanguage = languages.find(l => l.code === newChoice.locale);
        
        if (foundCurrency) {
          setSelectedCurrency(foundCurrency);
          cart.updateCurrency(foundCurrency.code, newChoice.countryCode);
          localStorage.setItem('user-currency-selection', newChoice.currency);
        }
        if (foundCountry) {
          setSelectedCountry(foundCountry);
        }
        if (foundLanguage) {
          setSelectedLanguage(foundLanguage);
        }
      }
    };

    window.addEventListener('user-locale-changed', handleLocaleChange);
    return () => {
      window.removeEventListener('user-locale-changed', handleLocaleChange);
    };
  }, []);

  const handlePreferencesToggle = () => {
    if (typeof window === 'undefined' || isProcessingCountryChange) return;
    
    const userChoice = localStorage.getItem('user-locale-choice');
    if (!userChoice) {
      console.log('No saved preferences, showing locale confirmation modal instead');
      return;
    }
    
    // Set temp preferences to current selections
    setTempPreferences({
      currency: selectedCurrency || allCurrencies[0],
      country: selectedCountry,
      language: languages.find(l => l.code === locale) || languages[0]
    });
    setIsPreferencesOpen(!isPreferencesOpen);
  };

  const handleSavePreferences = () => {
    if (!tempPreferences.currency || !tempPreferences.country) return;
    
    const countryChanged = tempPreferences.country.code !== selectedCountry.code;
    const currencyChanged = tempPreferences.currency.code !== selectedCurrency?.code;
    const languageChanged = tempPreferences.language.code !== locale;

    // If language ONLY changed, apply immediately without confirmation
    if (!countryChanged && languageChanged) {
      applyLanguageChange(tempPreferences.language);
      setIsPreferencesOpen(false);
    } 
    // If currency ONLY changed, apply immediately without confirmation
    else if (!countryChanged && currencyChanged) {
      applyCurrencyChange(tempPreferences.currency);
      setIsPreferencesOpen(false);
    }
    // If country changed, show confirmation
    else if (countryChanged) {
      // When country changes, validate currency compatibility
      let newCurrency = tempPreferences.currency;
      
      // If changing to Israel, force ILS
      if (tempPreferences.country.code === 'IL') {
        newCurrency = allCurrencies.find(c => c.code === 'ILS') || tempPreferences.currency;
      } 
      // If changing from Israel to another country, set default currency for that country
      else if (selectedCountry.code === 'IL') {
        const defaultCurrencyCode = getDefaultCurrencyForCountry(tempPreferences.country.code);
        newCurrency = allCurrencies.find(c => c.code === defaultCurrencyCode) || tempPreferences.currency;
      }
      // For all other country changes, keep the selected currency
      // (USD/EUR are valid for all non-IL countries)
      
      setPendingChanges({
        country: tempPreferences.country,
        currency: newCurrency || tempPreferences.currency,
        changes: {
          countryChanged: true
        }
      });
      setShowConfirmation(true);
      setIsPreferencesOpen(false);
    } else {
      // No changes
      setIsPreferencesOpen(false);
    }
  };

  const applyLanguageChange = (language: any) => {
    // Update language state
    setSelectedLanguage(language);
    
    // Update user locale choice with new language ONLY
    const userChoice = localStorage.getItem('user-locale-choice');
    let updatedChoice;
    
    if (userChoice) {
      const existingChoice = JSON.parse(userChoice);
      updatedChoice = {
        ...existingChoice,
        locale: language.code
      };
    } else {
      // If no existing choice, create new one with default values
      updatedChoice = {
        locale: language.code,
        currency: selectedCurrency?.code || 'USD',
        country: selectedCountry.name,
        countryCode: selectedCountry.code
      };
    }
    
    localStorage.setItem('user-locale-choice', JSON.stringify(updatedChoice));
    
    // Dispatch events to update other components
    window.dispatchEvent(
      new CustomEvent('user-locale-changed', {
        detail: updatedChoice,
      })
    );
    
    // Update router locale
    if (language.code !== locale) {
      router.push(router.pathname, router.asPath, { 
        locale: language.code 
      });
    }
  };

  const applyCurrencyChange = (currency: Currency) => {
    setSelectedCurrency(currency);
    
    localStorage.setItem('user-currency-selection', currency.code);
    // Update cart with new currency
    cart.updateCurrency(currency.code, selectedCountry.code);
    
    // Update user locale choice with new currency
    const userChoice = localStorage.getItem('user-locale-choice');
    let updatedChoice;
    
    if (userChoice) {
      const existingChoice = JSON.parse(userChoice);
      updatedChoice = {
        ...existingChoice,
        currency: currency.code
      };
    } else {
      updatedChoice = {
        locale: locale,
        currency: currency.code,
        country: selectedCountry.name,
        countryCode: selectedCountry.code
      };
    }
    
    localStorage.setItem('user-locale-choice', JSON.stringify(updatedChoice));
    
    // Dispatch events to update other components
    window.dispatchEvent(
      new CustomEvent('user-locale-changed', {
        detail: updatedChoice,
      })
    );
    
    if (!router.pathname.includes('/cart')) {
      const newQuery = { ...query, currency: currency.code };
      router.push(
        {
          pathname,
          query: newQuery,
        },
        undefined,
        { shallow: false }
      );
    }

    setTimeout(() => {
      window.location.reload();
    }, 300);
  };

  const applyCountryAndCurrencyChange = async (country: any, currency: Currency) => {
    console.log('[CurrencyDropdown] Starting country change to:', country.code);
    
    // Check if user is on a cart page and redirect if necessary
    if (isCartPage()) {
      console.log('[CurrencyDropdown] User on cart page, redirecting to homepage');
      
      // First update the state and localStorage
      setSelectedCountry(country);
      setSelectedCurrency(currency);
      
      localStorage.setItem('user-currency-selection', currency.code);
      
      // Update user locale choice
      const userChoice = localStorage.getItem('user-locale-choice');
      let updatedChoice;
      
      if (userChoice) {
        const existingChoice = JSON.parse(userChoice);
        updatedChoice = {
          ...existingChoice,
          currency: currency.code,
          country: country.name,
          countryCode: country.code
        };
      } else {
        updatedChoice = {
          locale: locale,
          currency: currency.code,
          country: country.name,
          countryCode: country.code
        };
      }
      
      localStorage.setItem('user-locale-choice', JSON.stringify(updatedChoice));
      
      // Update cart
      if (cart.updateCurrency) {
        cart.updateCurrency(currency.code, country.code);
      }
      
      // Dispatch event
      window.dispatchEvent(
        new CustomEvent('user-locale-changed', {
          detail: updatedChoice,
        })
      );
      
      // Clear any cart-related data
      localStorage.removeItem('selected-address');
      localStorage.removeItem('shipping-address');
      localStorage.removeItem('cart-items');
      localStorage.removeItem('cart');
      localStorage.removeItem('cart-data');
      
      // Redirect to homepage
      router.push('/');
      return;
    }
    
    // Normal country change flow for non-cart pages
    setIsProcessingCountryChange(true);
    setShowConfirmation(false);
    
    try {
      // Reset cart when country changes
      console.log('[CurrencyDropdown] Resetting cart before country change...');
      await resetCart();
      
      // Wait a bit to ensure all async operations complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('[CurrencyDropdown] Cart reset complete, updating country and currency...');
      
      setSelectedCountry(country);
      setSelectedCurrency(currency);
      
      localStorage.setItem('user-currency-selection', currency.code);
      
      // Update cart with both currency and country
      if (cart.updateCurrency) {
        cart.updateCurrency(currency.code, country.code);
      }
      
      // Update user locale choice with new country and currency
      const userChoice = localStorage.getItem('user-locale-choice');
      let updatedChoice;
      
      if (userChoice) {
        const existingChoice = JSON.parse(userChoice);
        updatedChoice = {
          ...existingChoice,
          currency: currency.code,
          country: country.name,
          countryCode: country.code
        };
      } else {
        updatedChoice = {
          locale: locale,
          currency: currency.code,
          country: country.name,
          countryCode: country.code
        };
      }
      
      localStorage.setItem('user-locale-choice', JSON.stringify(updatedChoice));
      
      // Dispatch events to update other components
      window.dispatchEvent(
        new CustomEvent('user-locale-changed', {
          detail: updatedChoice,
        })
      );
      
      console.log('[CurrencyDropdown] Country change completed, reloading page...');
      
      // Force reload to ensure fresh cart state
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      console.error('[CurrencyDropdown] Error during country change:', error);
      // Still try to proceed with the change
      setSelectedCountry(country);
      setSelectedCurrency(currency);
      
      // Reload anyway
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } finally {
      // Reset loading state after a delay (page will reload anyway)
      setTimeout(() => {
        setIsProcessingCountryChange(false);
      }, 2000);
    }
  };

  const confirmChanges = () => {
    if (!pendingChanges) return;
    
    applyCountryAndCurrencyChange(pendingChanges.country, pendingChanges.currency);
    
    // Close confirmation
    setShowConfirmation(false);
    setPendingChanges(null);
  };

  const cancelChanges = () => {
    setShowConfirmation(false);
    setPendingChanges(null);
  };

  const resetCart = async () => {
    if (typeof window === 'undefined') return;
    
    console.log('[CurrencyDropdown] Starting cart reset...');
    
    // Create a promise that resolves when cart is emptied
    const cartEmptiedPromise = new Promise((resolve) => {
      const handleCartEmptied = () => {
        console.log('[CurrencyDropdown] Cart emptied event received');
        window.removeEventListener('cart-emptied', handleCartEmptied);
        resolve(true);
      };
      
      window.addEventListener('cart-emptied', handleCartEmptied);
      
      // Set timeout in case cart emptying fails
      setTimeout(() => {
        console.log('[CurrencyDropdown] Cart emptying timeout - proceeding anyway');
        window.removeEventListener('cart-emptied', handleCartEmptied);
        resolve(true);
      }, 5000);
    });
    
    // Dispatch empty cart event
    window.dispatchEvent(new CustomEvent('empty-cart'));
    
    // Wait for cart to be emptied (or timeout)
    await cartEmptiedPromise;
    
    console.log('[CurrencyDropdown] Cart reset completed');
    
    // Clear local storage
    localStorage.removeItem('selected-address');
    localStorage.removeItem('shipping-address');
    localStorage.removeItem('cart-items');
    localStorage.removeItem('cart');
    localStorage.removeItem('cart-data');
    
    return true;
  };

  // Don't render if no selected currency
  if (!selectedCurrency) {
    return null;
  }

  const userCountryCode = getUserCountryCode();
  const availableCountries = getAvailableCountries();
  
  // Get available currencies based on selected temp country
  // This ensures USD and EUR are always shown for non-IL countries
  const availableCurrencies = getAvailableCurrenciesForCountry(tempPreferences.country?.code || userCountryCode);
  
  // Show currency dropdown only if not Israel
  const showCurrencyDropdown = tempPreferences.country?.code !== 'IL';

  // Prepare dropdown options
  const countryOptions = availableCountries.map(country => ({
    value: country.code,
    label: country.name,
    icon: <span className="text-lg mr-2">{country.flag}</span>,
    subtext: `Default: ${country.currency}`,
  }));

  // Currency options - NOW SHOWS BOTH USD AND EUR for all non-IL countries
  const currencyOptions = availableCurrencies.map(currency => ({
    value: currency.code,
    label: `${currency.code} - ${currency.name}`,
    icon: <span className="text-lg mr-2">{currency.symbol}</span>,
    subtext: `Country: ${currency.countryName}`,
  }));

  const languageOptions = languages.map(language => ({
    value: language.code,
    label: language.name,
    icon: <span className="text-lg mr-2">{language.flag}</span>
  }));

  // Dropdown animations
  const dropdownVariants = {
    hidden: { opacity: 0, y: -10, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: -10, scale: 0.95 }
  };

  return (
    <>
      <div className="relative inline-block text-left" ref={dropdownRef}>
        {/* Preferences button */}
        <button
          type="button"
          onClick={handlePreferencesToggle}
          disabled={isProcessingCountryChange}
          className={`
            flex ${locale === 'ar' ? 'flex-row-reverse' : 'flex-row'}
            w-full justify-between items-center
            rounded-md border border-gray-300
            ${isProcessingCountryChange ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:bg-gray-50'}
            px-3 py-2
            text-sm font-medium ${isProcessingCountryChange ? 'text-gray-500' : 'text-gray-700'}
            shadow-sm
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            transition-all duration-200
          `}
          dir={locale === 'ar' ? 'rtl' : 'ltr'}
        >
          <div className="flex items-center gap-2">
            {isProcessingCountryChange ? (
              <TailSpin
                height="24"
                width="24"
                color="#00214D"
                ariaLabel="tail-spin-loading"
                radius="1"
                visible={true}
              />
            ) : (
              <img
                alt={selectedCountry.code}
                src={`https://flagsapi.com/${selectedCountry.code}/flat/16.png`}
                className="w-4 h-4"
              />
            )}
            <span>{selectedCurrency.code}</span>
          </div>
          <svg
            className={`ml-2 h-4 w-4 transition-transform duration-200 ${isProcessingCountryChange ? 'opacity-50' : ''} ${isPreferencesOpen ? 'rotate-180' : ''}`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Preferences Dropdown Menu with Framer Motion */}
        <AnimatePresence>
          {isPreferencesOpen && (
            <motion.div
              ref={preferencesRef}
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={dropdownVariants}
              transition={{ duration: 0.15 }}
              className={`
                absolute z-[150] mt-2
                ${locale === 'ar' ? 'sm:left-0 left-4' : 'sm:right-0 right-4'}
                min-w-[280px] lg:max-w-[calc(100vw-2rem)]
                origin-top-right rounded-lg
                bg-white shadow-xl ring-1 ring-black ring-opacity-5
                focus:outline-none
                overflow-y-auto max-h-[calc(100vh-200px)]
              `}
            >
              <div className="p-4 sm:p-5 relative">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
                  {t('preferences') || 'Preferences'}
                </h3>
                
                {/* Ship To Custom Dropdown */}
                <div className="mb-4">
                  <CustomDropdown
                    options={countryOptions}
                    modalLanguage={locale}
                    type={'country'}
                    value={tempPreferences.country?.code || ''}
                    onChange={(value: any) => {
                      const country = availableCountries.find(c => c.code === value) || availableCountries[0];
                      let newCurrency = tempPreferences.currency;
                      
                      // Handle special cases for currency when country changes
                      if (country.code === 'IL') {
                        // If changing to Israel, force ILS
                        const ilsCurrency = allCurrencies.find(c => c.code === 'ILS');
                        if (ilsCurrency) {
                          newCurrency = ilsCurrency;
                        }
                      } else if (tempPreferences.country?.code === 'IL') {
                        // If changing FROM Israel to another country, set default currency
                        const defaultCurrencyCode = getDefaultCurrencyForCountry(country.code);
                        const defaultCurrency = allCurrencies.find(c => c.code === defaultCurrencyCode);
                        if (defaultCurrency) {
                          newCurrency = defaultCurrency;
                        }
                      }
                      // For all other cases (non-IL to non-IL), KEEP the selected currency
                      // This allows free switching between USD and EUR
                      
                      setTempPreferences(prev => ({ 
                        ...prev, 
                        country: country,
                        currency: newCurrency || prev.currency
                      }));
                    }}
                    placeholder="Select country"
                    label={t('shipTo') || 'Ship To'}
                    position={locale === 'ar' ? 'left' : 'right'}
                    hideDisabled={true}
                  />
                </div>

                {/* Currency Custom Dropdown - Shows USD and EUR for all non-IL countries */}
                {showCurrencyDropdown && (
                  <div className="mb-4">
                    <CustomDropdown
                      options={currencyOptions}
                      modalLanguage={locale}
                      type={'currency'}
                      value={tempPreferences.currency?.code || ''}
                      onChange={(value: any) => {
                        const currency = allCurrencies.find(c => c.code === value);
                        setTempPreferences(prev => ({ 
                          ...prev, 
                          currency: currency || null
                        }));
                        
                        // If currency changes to ILS, automatically set country to Israel
                        if (currency?.code === 'ILS') {
                          const israelCountry = countries.find(c => c.code === 'IL');
                          if (israelCountry) {
                            setTempPreferences(prev => ({ ...prev, country: israelCountry }));
                          }
                        }
                        // No automatic country change for USD/EUR selections
                        // User can freely switch between USD and EUR without changing country
                      }}
                      placeholder="Select currency"
                      label={t('currency') || 'Currency'}
                      position={locale === 'ar' ? 'left' : 'right'}
                      hideDisabled={true}
                    />
                  </div>
                )}

                {/* Language Custom Dropdown */}
                <div className="mb-6">
                  <CustomDropdown
                    options={languageOptions}
                    modalLanguage={locale}
                    type={'language'}
                    value={tempPreferences.language?.code || ''}
                    onChange={(value: any) => {
                      const language = languages.find(l => l.code === value) || languages[0];
                      setTempPreferences(prev => ({ ...prev, language }));
                    }}
                    placeholder="Select language"
                    label={t('language') || 'Language'}
                    position={locale === 'ar' ? 'left' : 'right'}
                    hideDisabled={true}
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleSavePreferences}
                    className="flex-1 bg-primary text-white text-md font-semibold py-3 rounded-lg hover:bg-primary-700 transition-colors duration-200"
                  >
                    {t('save') || 'Save'}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Confirmation Popup - ONLY for country changes */}
      <AnimatePresence>
        {showConfirmation && pendingChanges && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4 py-8"
            dir={locale === 'ar' ? 'rtl' : 'ltr'}
          >
            <motion.div
              ref={confirmationRef}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl max-w-md w-full p-6"
            >
              <div className="text-center">
                <h3 className="text-xl font-semibold text-[#272727] mt-4 px-6 mb-2">
                  {t('changeCountryHeading') || 'Change Country'}
                </h3>
                
                <div className="mt-2 p-4 text-[#8A99AD]">
                  <p className={`text-md font-medium mb-4 ${locale === 'ar' ? 'text-right' : 'text-left'}`}>
                    {t('changeCountryParagraphHeading') || 'Changing country will:'}
                  </p>
                  <ul className={`text-sm text-yellow-700 ${locale === 'ar' ? 'text-right' : 'text-left'} space-y-1`}>
                    <li>{t('paragraphListOne')}</li>
                    <li>{t('paragraphListTwo')}</li>
                    <li>{t('paragraphListThree')}</li>
                    <li>
                      {t('paragraphListFour')}
                    </li>
                  </ul>
                </div>

                {/* Show country and currency comparison */}
                <div className="flex items-center justify-around gap-4 mb-4 p-3">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <img
                        alt={selectedCountry.code}
                        src={`https://flagsapi.com/${selectedCountry.code}/shiny/64.png`}
                        className="w-full"
                      />
                    </div>
                    <p className="text-xs text-gray-500">{t('currentCountry') || 'Current Country'}</p>
                  </div>
                  <span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="16" viewBox="0 0 8 16" fill="none" className={`${locale === 'ar' ? '' : 'rotate-180'}`}>
                      <path d="M7.89453 14.9847C7.8943 14.7196 7.78879 14.4654 7.6012 14.278L2.48653 9.16338C2.33172 9.00861 2.20891 8.82486 2.12513 8.62262C2.04134 8.42038 1.99822 8.20362 1.99822 7.98471C1.99822 7.76581 2.04134 7.54904 2.12513 7.34681C2.20891 7.14457 2.33172 6.96082 2.48653 6.80605L7.59453 1.69471C7.77669 1.50611 7.87748 1.25351 7.87521 0.991311C7.87293 0.729114 7.76776 0.478301 7.58235 0.292893C7.39694 0.107485 7.14613 0.00231625 6.88393 3.78468e-05C6.62174 -0.00224056 6.36913 0.0985538 6.18053 0.280712L1.07253 5.38805C0.385708 6.07622 -3.11649e-05 7.00878 -3.11224e-05 7.98105C-3.10799e-05 8.95332 0.385708 9.88587 1.07253 10.574L6.1872 15.6887C6.32685 15.8285 6.50477 15.9237 6.6985 15.9624C6.89223 16.0012 7.09309 15.9816 7.27574 15.9063C7.45839 15.831 7.61463 15.7033 7.72476 15.5393C7.83489 15.3752 7.89396 15.1823 7.89453 14.9847Z" fill="#00214D"/>
                    </svg>
                  </span>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <img
                        alt={pendingChanges.country.code}
                        src={`https://flagsapi.com/${pendingChanges.country.code}/shiny/64.png`}
                        className="w-full"
                      />
                    </div>
                    <p className="text-xs text-gray-500">{t('newCountry') || 'New Country'}</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={cancelChanges}
                    className="flex-1 bg-white border border-gray-300 text-gray-700 text-md font-semibold py-3 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    {t('cancelButton') || 'Cancel'}
                  </button>
                  <button
                    onClick={confirmChanges}
                    className="flex-1 bg-primary text-white text-md font-semibold py-3 rounded-lg hover:bg-primary transition-colors duration-200"
                  >
                    {t('confirmChange') || 'Confirm Change'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}